import { useNavigate } from 'react-router-dom'
import { Zap, Banknote, ShieldCheck, MapPin } from 'lucide-react'
import { SearchBar } from '@/components/search/SearchBar'
import { toURLSearchParams, resolveSearchParams } from '@/lib/schemas/search'
import type { SearchParams, ResolvedSearchParams } from '@/lib/schemas/search'
import { APP_NAME, APP_TAGLINE } from '@/lib/constants'
import { Logo } from '@/components/ui/Logo'

// Booking-style destination tiles. Photos are free-licensed (Wikimedia Commons /
// Wikipedia leads — see public/destinations/CREDITS.json for author + license).
// The gradient sits behind each image as a fallback if it ever fails to load;
// gradient classes are literals so Tailwind keeps them at build time.
const FEATURED_CITIES = [
  { name: 'Manila',    province: 'NCR',     hint: 'The capital',           image: '/destinations/manila.jpg',  gradient: 'from-slate-700 to-slate-500' },
  { name: 'Cebu City', province: 'Cebu',    hint: 'Queen of the South',    image: '/destinations/cebu.jpg',    gradient: 'from-emerald-700 to-emerald-500' },
  { name: 'Boracay',   province: 'Aklan',   hint: 'World-famous beach',    image: '/destinations/boracay.png', gradient: 'from-sky-600 to-cyan-400' },
  { name: 'El Nido',   province: 'Palawan', hint: 'Island paradise',       image: '/destinations/el-nido.jpg', gradient: 'from-teal-700 to-cyan-500' },
  { name: 'Tagaytay',  province: 'Cavite',  hint: 'Cool highland retreat', image: '/destinations/tagaytay.jpg', gradient: 'from-green-700 to-lime-500' },
  { name: 'Panglao',   province: 'Bohol',   hint: 'Diving & white sand',   image: '/destinations/panglao.jpg', gradient: 'from-blue-700 to-cyan-500' },
]

const VALUE_PROPS = [
  {
    Icon:   Zap,
    term:   'Instant confirmation',
    detail: 'Bookings confirmed in real time — no waiting, no back-and-forth.',
  },
  {
    Icon:   Banknote,
    term:   'Best available rates',
    detail: 'Direct rates from properties across the Philippines, with flexible cancellation options.',
  },
  {
    Icon:   ShieldCheck,
    term:   'Secure payments',
    detail: 'Pay safely via PayMongo. Your funds are held securely until after checkout.',
  },
]

export default function Home() {
  const navigate = useNavigate()

  function handleSearch(values: Partial<ResolvedSearchParams>) {
    const resolved = resolveSearchParams(values as SearchParams)
    navigate(`/search?${toURLSearchParams(resolved).toString()}`)
  }

  function handleCityClick(city: string, province?: string) {
    const resolved = resolveSearchParams({ city, province })
    navigate(`/search?${toURLSearchParams(resolved).toString()}`)
  }

  return (
    <div className="flex flex-col">

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section className="relative flex min-h-[72vh] flex-col items-center justify-center bg-kasa-green-600 px-4 py-24">

        {/* Background decorations — clipped to the hero so they don't bleed,
            while the section itself stays un-clipped so the destination
            dropdown can overflow below it. */}
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">

        {/* Subtle dot-grid pattern for visual texture */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.12) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />

        {/* Soft radial glow behind search card */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_60%,rgba(45,90,69,0.7),transparent)]"
        />

        {/* Philippine scenery silhouette — 9–10% white, anchored to bottom, wave paints on top */}
        <svg
          aria-hidden
          className="pointer-events-none absolute bottom-0 left-0 w-full"
          viewBox="0 0 1440 200"
          preserveAspectRatio="xMidYMax slice"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Rolling coastal terrain — base layer */}
          <path
            d="M0 185 Q180 155 360 172 Q540 188 720 160 Q900 130 1080 155 Q1260 178 1440 152 L1440 200 L0 200 Z"
            fill="rgba(255,255,255,0.07)"
          />
          {/* Background island humps */}
          <ellipse cx="175" cy="178" rx="95" ry="18" fill="rgba(255,255,255,0.07)"/>
          <ellipse cx="1195" cy="165" rx="105" ry="22" fill="rgba(255,255,255,0.07)"/>

          <g fill="rgba(255,255,255,0.10)">
            {/* Palm tree 1 — trunk */}
            <path d="M291 185 Q293 163 297 136 L303 136 Q299 163 302 185 Z"/>
            {/* fronds as ellipses rotated around trunk top (297,136) */}
            <ellipse cx="297" cy="116" rx="3.5" ry="20" transform="rotate(-60 297 136)"/>
            <ellipse cx="297" cy="116" rx="3" ry="20" transform="rotate(-30 297 136)"/>
            <ellipse cx="297" cy="116" rx="3" ry="20" transform="rotate(0 297 136)"/>
            <ellipse cx="297" cy="116" rx="3" ry="20" transform="rotate(30 297 136)"/>
            <ellipse cx="297" cy="116" rx="3.5" ry="20" transform="rotate(60 297 136)"/>

            {/* Nipa hut body */}
            <path d="M406 190 L406 163 L462 163 L462 190 Z"/>
            {/* Steep thatched roof */}
            <path d="M390 167 L434 126 L478 167 Z"/>
            {/* Roof overhang ledge */}
            <rect x="388" y="166" width="91" height="5" rx="1"/>
            {/* Door — slightly darker so the opening reads */}
            <path d="M424 176 L424 190 L444 190 L444 176 Z" fill="rgba(27,67,50,0.4)"/>

            {/* Resort bungalows */}
            <rect x="566" y="173" width="40" height="17"/>
            <path d="M557 175 L586 153 L615 175 Z"/>
            <rect x="622" y="173" width="40" height="17"/>
            <path d="M613 175 L642 155 L671 175 Z"/>

            {/* Hotel / resort building — center */}
            <rect x="736" y="105" width="90" height="85"/>
            <rect x="727" y="102" width="108" height="7"/>
            <rect x="750" y="97" width="62" height="8"/>
            {/* Roof gable */}
            <path d="M755 97 L781 84 L807 97 Z"/>

            {/* Palm tree 2 — trunk */}
            <path d="M1081 185 Q1083 163 1087 136 L1093 136 Q1089 163 1092 185 Z"/>
            <ellipse cx="1087" cy="116" rx="3.5" ry="20" transform="rotate(-60 1087 136)"/>
            <ellipse cx="1087" cy="116" rx="3" ry="20" transform="rotate(-30 1087 136)"/>
            <ellipse cx="1087" cy="116" rx="3" ry="20" transform="rotate(0 1087 136)"/>
            <ellipse cx="1087" cy="116" rx="3" ry="20" transform="rotate(30 1087 136)"/>
            <ellipse cx="1087" cy="116" rx="3.5" ry="20" transform="rotate(60 1087 136)"/>

            {/* Condo / apartment tower */}
            <rect x="1260" y="118" width="56" height="72"/>
            <rect x="1252" y="115" width="72" height="7"/>
            <rect x="1270" y="104" width="36" height="16"/>
            <rect x="1264" y="101" width="48" height="6"/>
          </g>
        </svg>

        {/* Decorative bottom wave — paints on top of silhouette */}
        <svg
          aria-hidden
          className="pointer-events-none absolute bottom-0 left-0 w-full"
          viewBox="0 0 1440 64"
          preserveAspectRatio="none"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 32 C360 64 1080 0 1440 32 L1440 64 L0 64 Z"
            fill="rgb(249,250,251)"
            fillOpacity="1"
          />
        </svg>
        </div>

        <div className="relative z-10 mx-auto flex max-w-4xl flex-col items-center gap-6 text-center">
          <Logo variant="icon" color="white" className="h-16 w-16 sm:h-20 sm:w-20" />

          <div>
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              {APP_NAME}
            </h1>
            <p className="mt-3 text-lg text-kasa-green-100 sm:text-xl">{APP_TAGLINE}</p>
          </div>

          <SearchBar
            onSearch={handleSearch}
            variant="hero"
            className="w-full"
          />
        </div>
      </section>

      {/* ── Popular destinations ─────────────────────────────────────────── */}
      {/* -mt-px slides this up 1px to swallow the hero wave's antialiased bottom
          edge, which otherwise renders as a faint hairline seam. */}
      <section className="relative -mt-px bg-gray-50 py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-end justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Popular destinations</h2>
              <p className="mt-1 text-sm text-gray-500">
                Top spots travellers are booking right now
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/search')}
              className="hidden text-sm font-medium text-kasa-green-600 hover:underline sm:block"
            >
              See all →
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {FEATURED_CITIES.map(({ name, province, hint, image, gradient }) => (
              <button
                key={name}
                type="button"
                onClick={() => handleCityClick(name, province)}
                className="group relative overflow-hidden rounded-2xl shadow-md transition-all hover:-translate-y-1 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kasa-green-500 focus-visible:ring-offset-2"
                style={{ aspectRatio: '3/4' }}
              >
                {/* Gradient fallback (shows while the photo loads or if it fails) */}
                <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`} />

                {/* Photo */}
                <img
                  src={image}
                  alt={`${name}, ${province}`}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />

                {/* Bottom shade so the label stays legible */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                {/* Location pin */}
                <div className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 backdrop-blur-sm">
                  <MapPin className="h-3 w-3 text-white" />
                  <span className="text-[10px] font-medium text-white">{province}</span>
                </div>

                {/* Name + hint */}
                <div className="absolute bottom-0 left-0 right-0 p-3 text-left">
                  <p className="text-sm font-bold text-white leading-tight">{name}</p>
                  <p className="mt-0.5 text-[10px] text-white/80 leading-snug">{hint}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Value props ──────────────────────────────────────────────────── */}
      <section className="bg-white py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="mb-10 text-center text-xl font-bold text-gray-900">
            Why book with KasaGO?
          </h2>
          <dl className="grid grid-cols-1 gap-8 sm:grid-cols-3">
            {VALUE_PROPS.map(({ Icon, term, detail }) => (
              <div key={term} className="flex flex-col gap-3">
                <span
                  className="flex h-11 w-11 items-center justify-center rounded-xl bg-kasa-green-100"
                  aria-hidden
                >
                  <Icon className="h-5 w-5 text-kasa-green-700" />
                </span>
                <dt className="text-base font-semibold text-kasa-green-700">{term}</dt>
                <dd className="text-sm leading-relaxed text-gray-600">{detail}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* ── Owner CTA ────────────────────────────────────────────────────── */}
      <section className="bg-kasa-green-600 py-16 text-center">
        <div className="mx-auto max-w-2xl px-4">
          <h2 className="text-2xl font-bold text-white sm:text-3xl">
            List your property on KasaGO
          </h2>
          <p className="mt-3 text-kasa-green-100">
            Reach travellers across the Philippines and grow your business with our
            easy-to-use extranet and automated payouts.
          </p>
          <a
            href="/list-your-property"
            className="mt-6 inline-block rounded-lg bg-kasa-amber px-8 py-3 text-sm font-semibold text-white hover:bg-kasa-amber-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kasa-amber"
          >
            Get started for free
          </a>
        </div>
      </section>
    </div>
  )
}
